/**
 * SignStep — Stage 6.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Officer previews the DisclosurePackage (resolved from cart state +
 * `config.disclosures` + `config.entityRules[].requiredDocs`), then clicks
 * <Start signing>.  Pressing Start mounts the SigningSurfaceDispatcher which
 * routes every (signer × doc) pair through the right capture engine:
 *
 *   - internal click-to-sign for ESIGN + Reg E + opt-ins (Q1)
 *   - internal typed signature for low-stakes disclosures (Q1)
 *   - internal drawn signature for signature cards / resolutions / BO + Trust
 *     certs (Q1) — with mobile-fallback QR for non-touch desktops
 *   - SignNow embedded iframe for online-cart surface
 *   - SignNow email/SMS invite for rcp-remote
 *   - WetSignScanner for wet-attested
 *
 * Per-signer-per-doc engine selection is driven by the admin-editable
 * `signing-engine-routing` table (Q3 + Q10). The dispatcher calls
 * `client.retail.signing.routing.choosePerSignerEngine` per pair, falling back
 * to a local cohort × doctype × surface table if the backend wrapper isn't
 * yet wired (see SigningSurfaceDispatcher for contract notes).
 *
 * Ceremony order (S1 punch-list 2026-06-11, mirrors the SPFx PhaseProgress
 * pipeline: CIF → numbers → documents → SignPause → accounts):
 *   1. CommitFlow's "Submit for opening" first runs POST /signing/start
 *      (creates the SigningSession row + binds cart.SigningGroupId), then
 *      POST /v3/carts/{id}/commit. The pipeline runs steps 1-5 and pauses
 *      with cart.status = 'awaiting-signatures'.
 *   2. THIS step mounts during that pause — the FSR runs the capture
 *      ceremony via SigningSurfaceDispatcher.
 *   3. `/v3/signing/internal/complete` (or the SignNow webhook) flips the
 *      SigningSession to 'completed' and enqueues the pipeline resume
 *      (AcctAdd onward). CommitFlow's status poll carries the FSR to
 *      'committed'.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CartDto,
  CommitStatus,
  SigningSessionConfig,
  SigningSignerConfig,
  SigningSignerMode,
  SigningDeliveryMode,
  OfficerPackageDocumentInfo,
} from '@retail-ops/shared-types';
import { Banner, Button, ProcessingOverlay, type ProcessingStep } from '@/components/primitives';
import {
  ConfigureSigningSessionModal,
  REMOTE_SIGNING_ENABLED,
} from '../components/ConfigureSigningSessionModal';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useEmitEvent } from '../hooks/useEmitEvent';
import type { RaoPerson } from '../types';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import {
  resolveDisclosures,
  buildPackage,
  type BuiltPackage,
} from '../disclosure';
import { parsePeople, parseProducts, parseEntityType } from '../utils/cartStateParse';
import { openingNeverWaits } from '../utils/signingCommitPolicy';
import { DisclosurePackagePanel } from '../components/disclosure/DisclosurePackagePanel';
import { isMinorPerson } from '../disclosure/PackageBuilder';
import { RemoteInvitesPanel, type RemoteInviteRow } from '../components/RemoteInvitesPanel';
import {
  SigningSurfaceDispatcher,
  type SignerInfo,
  type DocInfo,
  type SigningSurface,
} from '@/components/signing/SigningSurfaceDispatcher';

export interface SignStepProps {
  cart: CartDto;
  /** Invoked when the dispatcher begins capturing signatures. */
  onSigningStarted?: (sessionId: string) => void;
  /**
   * Invoked when the dispatcher reports every (signer × doc) pair complete.
   * CartDetail wires this to advance the stage ribbon to Commit.
   */
  onAllSigned?: (sessionId: string) => void;
  /** BACKLOG-1673 — true while the in-branch capture is running (the parent keeps this step mounted). */
  onCeremonyActiveChange?: (active: boolean) => void;
}

// ---------------------------------------------------------------------------
// Cart-state parsing — moved to ../utils/cartStateParse (shared with
// CommitFlow's pre-commit /signing/start call, S1 punch-list 2026-06-11).
// ---------------------------------------------------------------------------

/**
 * Map a v3 CustomerType to a cohortKey used by the routing table.
 * Mirrors the cohort partition documented in entity-groups memory.
 */
function entityTypeToCohortKey(entityType: CustomerType | string | undefined): string {
  if (!entityType) return 'personal';
  const t = String(entityType);
  // Trust subtypes drive most per-cohort routing rules (Q10).
  if (t === 'RevTrust' || t === 'RevocableTrust') return 'trust-revocable';
  if (t === 'IrrTrust' || t === 'IrrevocableTrust') return 'trust-irrevocable';
  if (t === 'SelfSettledSNT' || t === 'SNT') return 'trust-self-settled-snt';
  if (t === 'LLC') return 'business-llc';
  if (t === 'SoleProp' || t === 'SoleProprietor') return 'business-sole-prop';
  if (t === 'Partnership') return 'business-partnership';
  if (t === 'Corporation' || t === 'Corp') return 'business-corporation';
  if (t === 'Estate') return 'estate';
  if (t === 'IOLTA') return 'iolta';
  if (t === 'PublicFunds') return 'gov-public-funds';
  if (t === 'PoliticalCampaign') return 'gov-political-campaign';
  if (t === 'Minor' || t === 'UTMA' || t === 'Custodial') return 'fiduciary';
  return 'personal';
}

/**
 * Map a built-package signer + cart's cifIds into the SignerInfo shape the
 * dispatcher consumes. Primary owner gets cifIds[0]; Joint signers slot
 * into subsequent indexes in cart.cifIds when present.
 */
function buildDispatcherSigners(
  pkg: BuiltPackage,
  people: ReadonlyArray<RaoPerson>
): ReadonlyArray<SignerInfo> {
  // 2026-07-02 — bind by ORDER, not display-name equality. pkg.signers is
  // built by PackageBuilder.buildSigners from `people` filtered to
  // primary/joint IN ARRAY ORDER, so index-zip against the same derivation is
  // deterministic. The old name-match broke on middle names/suffix/casing and
  // left cifNumber='' (capture hard-blocked). Name-match remains the fallback
  // for defensive coverage only.
  // Minors are never signers — pkg.signers leaves them out, so this list must too, or the adult signer is
  // paired with the minor's CIF (2026-10-06: Alan A Test's invite carried Grace's CIF; the registered-e-mail
  // check then found no e-mail). Same rule as PackageBuilder.isMinorPerson.
  const signerPeople = people.filter((p) => (p.role === 'primary' || p.role === 'joint') && !isMinorPerson(p));
  return pkg.signers.map((s, idx): SignerInfo => {
    const byIndex = signerPeople[idx];
    const byName = people.find(
      (p) =>
        (p.fullName && s.Name && p.fullName.toLowerCase() === s.Name.toLowerCase()) ||
        (p.firstName + ' ' + p.lastName).toLowerCase() === s.Name.toLowerCase()
    );
    // An exact name match is the stronger evidence; position is the fallback.
    const match = byName ?? byIndex;
    const firstName = s.FirstName ?? match?.firstName ?? s.Name.split(/\s+/)[0] ?? '';
    const lastName = s.LastName ?? match?.lastName ?? s.Name.split(/\s+/).slice(1).join(' ');
    const cifNumber = match?.cifId ?? '';
    return {
      signerIndex: idx,
      displayName: s.Name,
      firstName,
      lastName,
      email: s.Email,
      ...(s.Phone ? { phone: s.Phone } : {}),
      cifNumber,
    };
  });
}

/**
 * Map a built-package document into the dispatcher's DocInfo shape. The
 * dispatcher resolves the routing-table engine per docTypeCode — we use
 * the templateCode as the doctype identity (matches the routing table's
 * docTypeCode column).
 */
function buildDispatcherDocs(pkg: BuiltPackage): ReadonlyArray<DocInfo> {
  return pkg.documents
    .filter((d) => !d.compileError)
    .map((d): DocInfo => ({
      documentInstanceId: d.instanceId,
      docTypeCode: d.templateCode,
      documentTitle: d.title,
    }));
}

// ---------------------------------------------------------------------------
// Signing-session config (Bill 2026-07-08) — the per-signer plan the FSR sets
// in ConfigureSigningSessionModal, persisted on cart.metadataJson.signingConfig.
// ---------------------------------------------------------------------------

/** Re-hydrate a persisted SigningSessionConfig off cart.metadataJson. */
function parseSigningConfig(cart: CartDto): SigningSessionConfig | null {
  if (!cart.metadataJson) return null;
  try {
    const raw = JSON.parse(cart.metadataJson) as Record<string, unknown>;
    const sc = raw['signingConfig'];
    if (sc && typeof sc === 'object' && Array.isArray((sc as SigningSessionConfig).signers)) {
      return sc as SigningSessionConfig;
    }
  } catch {
    /* malformed metadata — treat as no prior config */
  }
  return null;
}

function hasSignerEmail(email: string | undefined): boolean {
  return !!email && email.trim().length > 0;
}

/** Delivery default follows the mode; email needs an address on file (Decision 2). */
function defaultDelivery(mode: SigningSignerMode, emailAvailable: boolean): SigningDeliveryMode {
  if (mode === 'remote') return emailAvailable ? 'email' : 'print-mail';
  return 'print-hand';
}

/**
 * Instant-initiate config used when the modal is SKIPPED:
 *   - single in-branch signer → [{ in-branch / drawn / print-hand }]
 *   - "Send for remote signing" (online carts, BACKLOG-0848)
 *                             → every signer remote / typed / email
 *                               (print-mail fallback when no email on file)
 */
function buildDefaultConfig(
  signers: ReadonlyArray<SignerInfo>,
  isOnlineCart: boolean,
  configuredBy?: string,
): SigningSessionConfig {
  const mode: SigningSignerMode = isOnlineCart ? 'remote' : 'in-branch';
  return {
    version: 1,
    signers: signers.map((s): SigningSignerConfig => {
      const emailAvail = hasSignerEmail(s.email);
      return {
        signerIndex: s.signerIndex,
        ...(s.cifNumber ? { cifId: s.cifNumber } : {}),
        ...(s.displayName ? { displayName: s.displayName } : {}),
        ...(emailAvail ? { email: s.email } : {}),
        mode,
        capture: mode === 'in-branch' ? 'drawn' : 'typed',
        delivery: defaultDelivery(mode, emailAvail),
      };
    }),
    anyRemote: isOnlineCart,
    configuredAt: new Date().toISOString(),
    ...(configuredBy ? { configuredBy } : {}),
  };
}

/**
 * Spinner + line of text. Bill 2026-09-17: while background work runs the FSR
 * needs to see movement and be told not to interrupt it, not a static message
 * that reads as a lock-up. Keyframes: styles/primitives.css (pcb-spin).
 */
function BusyLine({ text }: { text: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
      <span
        aria-hidden
        style={{
          width: 14, height: 14, flexShrink: 0,
          border: '2px solid var(--pcb-blue)',
          borderTopColor: 'transparent', borderRadius: '50%',
          display: 'inline-block', animation: 'pcb-spin 0.7s linear infinite',
        }}
      />
      <span>{text}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SignStep({ cart, onSigningStarted, onAllSigned, onCeremonyActiveChange }: SignStepProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const { config } = useConfig();
  const { user } = useAuth();
  const emit = useEmitEvent();

  const [pkg, setPkg] = useState<BuiltPackage | null>(null);
  const [pkgLoading, setPkgLoading] = useState<boolean>(false);
  const [pkgError, setPkgError] = useState<string | null>(null);
  const [pkgVersion, setPkgVersion] = useState<number>(0);

  // Dispatcher gate — FSR clicks Start to mount the dispatcher inline.
  const [signingActive, setSigningActive] = useState<boolean>(false);
  const [allComplete, setAllComplete] = useState<boolean>(false);

  // BACKLOG-1482 (Bill 2026-09-17) — closing the signing session is what starts
  // the account opening. Its failure used to go to console.error only, and the
  // FSR was told "the pipeline resumes automatically" either way. Teams has no
  // console, so a failed close was invisible. Track the outcome, and while the
  // accounts post, show the live pipeline instead of a static green banner.
  // waitingRemote (Bill 2026-10-14 smoke cart): the in-branch signers are done but a remote signer
  // is not — the server refuses to close the session until they finish, then closes it itself.
  const [completeState, setCompleteState] =
    useState<{ ok: boolean; skipped?: boolean; waitingRemote?: boolean; message?: string } | null>(null);
  const [retryingComplete, setRetryingComplete] = useState<boolean>(false);
  const [pipelineStatus, setPipelineStatus] = useState<CommitStatus | null>(null);
  const [pipelineSlow, setPipelineSlow] = useState<boolean>(false);

  // Configure-signing-session modal (Bill 2026-07-08) + the resolved per-signer
  // plan (drives the metadataJson persist + the dispatcher's capture prefs).
  const [configureOpen, setConfigureOpen] = useState<boolean>(false);
  // Who must sign — from the SERVER's resolve (the same rule the ceremony and
  // the completeness gate use, incl. tinHolder / check-order widening), mapped
  // role → signer index by roster position (Primary=0, Joint1=1, …).
  const [requiredSignerIndexes, setRequiredSignerIndexes] = useState<ReadonlySet<number> | undefined>(undefined);
  const [requiredLoading, setRequiredLoading] = useState<boolean>(false);
  // Blocking progress surfaces (Bill 2026-09-30): session start + post-signature.
  const [startPhase, setStartPhase] = useState<'plan' | 'prepare' | 'invites' | null>(null);
  const [startBeganAt, setStartBeganAt] = useState<number | null>(null);
  const [finishBeganAt, setFinishBeganAt] = useState<number | null>(null);
  const [pipelineHandedOff, setPipelineHandedOff] = useState<boolean>(false);
  useEffect(() => { onCeremonyActiveChange?.(signingActive && !allComplete); }, [signingActive, allComplete, onCeremonyActiveChange]);
  const [resolvedConfig, setResolvedConfig] = useState<SigningSessionConfig | null>(null);

  // 2026-07-01 (smoke-preflight fix) — the ceremony signs the SERVER's
  // package. /v3/signing/internal/prepare returns the document instance ids
  // the server rendered + cached at /signing/start; the capture endpoint
  // accepts ONLY those ids. The client-built package (pkg above) remains the
  // FSR preview, but the dispatcher's doc list comes from the server.
  const [serverDocs, setServerDocs] = useState<ReadonlyArray<DocInfo> | null>(null);
  const [starting, setStarting] = useState<boolean>(false);
  const [startError, setStartError] = useState<string | null>(null);

  // Remote-signing invites (BACKLOG-0832, Bill 2026-07-13 Item 23). One row
  // per remote signer: the emailed secure link + the one-time access code the
  // FSR relays out-of-band + delivery status.
  const [remoteInvites, setRemoteInvites] = useState<ReadonlyArray<RemoteInviteRow>>([]);
  // Reopening the cart (minutes or days later) shows its open remote invites so the officer can send a
  // reminder, correct the e-mail and resend, or cancel (Bill 2026-10-06).
  // The list is re-read every 20 s while a signer is still working, so the officer watches each
  // remote signer's progress live (BACKLOG-1760); progress fields merge into rows already on screen
  // (a just-sent invite keeps its link).
  const [invitePoll, setInvitePoll] = useState(0);
  useEffect(() => {
    let cancelled = false;
    client.retail.signing.remote.listOpen(cart.cartId)
      .then((res) => {
        if (cancelled || !res.success || !res.invites || res.invites.length === 0) return;
        const fresh = res.invites.map((i, n) => ({
          signerIndex: i.signerIndex ?? n,
          presentationId: i.presentationId,
          name: i.name ?? `Signer ${(i.signerIndex ?? n) + 1}`,
          ...(i.email ? { email: i.email } : {}),
          emailSent: !!i.inviteSentAt,
          ...(i.inviteSentAt ? { sentAt: i.inviteSentAt } : {}),
          expiresAt: i.expiresAt,
          ...(i.stage ? { stage: i.stage } : {}),
          signedCount: i.signedCount ?? 0,
          ...(typeof i.toSignCount === 'number' ? { toSignCount: i.toSignCount } : {}),
          ...(i.lastActivityAt ? { lastActivityAt: i.lastActivityAt } : {}),
        }));
        setRemoteInvites((prev) => {
          if (prev.length === 0) return fresh;
          return prev.map((row) => {
            const f = fresh.find((x) => x.signerIndex === row.signerIndex);
            return f && !row.cancelled
              ? { ...row, presentationId: f.presentationId, stage: f.stage, signedCount: f.signedCount, toSignCount: f.toSignCount, lastActivityAt: f.lastActivityAt }
              : row;
          });
        });
      })
      .catch(() => { /* the panel keeps what it has */ });
    return () => { cancelled = true; };
  }, [client, cart.cartId, invitePoll]);
  const invitesInFlight = remoteInvites.some((r) => !r.cancelled && r.stage !== 'finished');
  useEffect(() => {
    if (!invitesInFlight) return undefined;
    const t = window.setInterval(() => setInvitePoll((n) => n + 1), 20_000);
    return () => window.clearInterval(t);
  }, [invitesInFlight]);
  const [fullyRemote, setFullyRemote] = useState<boolean>(false);
  // Reopened cart whose saved plan is all-remote and whose invites are out: the session is already
  // running, so do not offer to start it again (that would re-send every invite).
  useEffect(() => {
    if (remoteInvites.length === 0) return;
    const saved = parseSigningConfig(cart);
    if (saved && saved.signers.length > 0 && saved.signers.every((x) => x.mode === 'remote')) setFullyRemote(true);
  }, [remoteInvites.length, cart]);

  // Phase A (Bill 2026-07-07) — server-resolved kiosk presentation config
  // from /v3/signing/internal/prepare, passed through to the dispatcher.
  const [presentationMode, setPresentationMode] = useState<
    'fsr-inline' | 'kiosk-optional' | 'kiosk-default' | null
  >(null);
  const [signerPortalBaseUrl, setSignerPortalBaseUrl] = useState<string | null>(null);
  // Who reviews the no-signature disclosures in branch (server prepare, Bill 2026-10-15).
  const [reviewSigners, setReviewSigners] = useState<ReadonlySet<number> | undefined>(undefined);

  // Derive cart slice + resolved disclosures.
  const people = useMemo(() => parsePeople(cart), [cart]);
  const products = useMemo(() => parseProducts(cart), [cart]);

  // C4 / TechReq#21 — CIF strip + reserved account numbers for the package summary.
  const disclosureParties = useMemo(
    () => people.map((p) => ({
      cifId: p.cifId ?? null,
      name: p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || 'Unnamed',
    })),
    [people],
  );
  const disclosureAccounts = useMemo(
    () => products
      .filter((p) => !!p.acctId)
      .map((p) => ({
        label: config.products.find((cp) => cp.code === p.code)?.name ?? p.code,
        acctId: p.acctId!,
      })),
    [products, config.products],
  );
  const entityType = useMemo(() => parseEntityType(cart), [cart]);

  const resolved = useMemo(
    () => resolveDisclosures({
      ...(entityType ? { entityType } : {}),
      products,
      people,
      ...(cart.branch ? { branchCode: cart.branch } : {}),
      disclosureTemplates: config.disclosures,
      entityRules: config.entityRules,
      entityRequiredDocs: config.entityRequiredDocs,
      productCatalog: config.products,
    }),
    [entityType, products, people, cart.branch, config.disclosures, config.entityRules, config.entityRequiredDocs, config.products],
  );

  // BACKLOG-1750: the server BUILT the package at approval. Read its manifest first; when it exists
  // the panel shows it and the client builder runs WITHOUT compiling (preview-only: signer roster and
  // counts only) - the documents signers see always come from the server (prepare).
  // undefined = still loading, null = no built package (fall back to the live compile).
  const [builtManifest, setBuiltManifest] = useState<ReadonlyArray<OfficerPackageDocumentInfo> | null | undefined>(undefined);
  const [manifestVersion, setManifestVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    client.signing.getOfficerPackageManifest(cart.cartId)
      .then((res) => { if (!cancelled) setBuiltManifest(res.success && res.data && res.data.length > 0 ? res.data : null); })
      .catch(() => { if (!cancelled) setBuiltManifest(null); });
    return () => { cancelled = true; };
  }, [client, cart.cartId, manifestVersion]);
  const haveBuilt = !!builtManifest && builtManifest.length > 0;

  // Build package — fan out compile calls (only when there is no built package).
  useEffect(() => {
    if (builtManifest === undefined) return undefined;
    let cancelled = false;
    setPkgLoading(true);
    setPkgError(null);
    const signal = makeSignal();
    (async () => {
      try {
        const built = await buildPackage({
          ...(haveBuilt ? { mode: 'preview-only' as const } : {}),
          resolved,
          disclosureTemplates: config.disclosures,
          disclosureVersions: config.disclosureVersions,
          people,
          ...(user?.upn ? { fsrEmail: user.upn } : {}),
          ...(user?.displayName ? { fsrName: user.displayName } : {}),
          client,
          signal,
        });
        if (!cancelled) setPkg(built);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!cancelled) setPkgError(err instanceof Error ? err.message : 'Unknown build error');
      } finally {
        if (!cancelled) setPkgLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resolved, config.disclosures, people, user?.upn, user?.displayName, client, makeSignal, pkgVersion, builtManifest, haveBuilt]);

  const refreshPackage = (): void => {
    setManifestVersion((v) => v + 1);
    setPkgVersion((v) => v + 1);
  };

  // Derived inputs for the dispatcher.
  const dispatcherSigners = useMemo(
    () => (pkg ? buildDispatcherSigners(pkg, people) : []),
    [pkg, people]
  );
  const dispatcherDocs = useMemo(() => (pkg ? buildDispatcherDocs(pkg) : []), [pkg]);
  // Only the signers signing IN BRANCH go to the signing screen (Bill 2026-10-14 smoke cart: Amy
  // was marked remote, got her e-mailed link, and the in-branch screen still opened on her turn).
  // Remote = the officer's plan says so, or the signer has a live remote link (the server reads
  // the same two signals: InternalSigningService.SignerIsRemoteAsync).
  const existingConfig = useMemo(() => parseSigningConfig(cart), [cart]);
  const remoteSignerIndexes = useMemo(() => {
    const set = new Set<number>();
    // The plan chosen this session, else the one saved on the cart (a reopened cart, BACKLOG-1761).
    for (const s of (resolvedConfig ?? existingConfig)?.signers ?? []) if (s.mode === 'remote') set.add(s.signerIndex);
    for (const r of remoteInvites) if (!r.cancelled && !r.error) set.add(r.signerIndex);
    return set;
  }, [resolvedConfig, existingConfig, remoteInvites]);
  const inBranchSigners = useMemo(
    () => dispatcherSigners.filter((s) => !remoteSignerIndexes.has(s.signerIndex)),
    [dispatcherSigners, remoteSignerIndexes],
  );
  const remoteInvitesRef = useRef(remoteInvites);
  remoteInvitesRef.current = remoteInvites;
  const handleCompleteResult = useCallback(
    (r: { ok: boolean; skipped?: boolean; message?: string }): void => {
      const remotePending = remoteInvitesRef.current.some((x) => !x.cancelled && !x.error && x.stage !== 'finished');
      if (!r.ok && remotePending) {
        setCompleteState({ ok: true, skipped: true, waitingRemote: true });
        return;
      }
      setCompleteState(r);
    },
    [],
  );
  const cohortKey = useMemo(() => entityTypeToCohortKey(entityType), [entityType]);
  // In-branch is the default + safest surface for the Teams shell. RCP /
  // online-cart surfaces will be mounted from their own host routes.
  const surface: SigningSurface = 'in-branch';

  const signerCount = dispatcherSigners.length;
  const noSigners = signerCount === 0;
  const docCount = dispatcherDocs.length;
  // BACKLOG-1671 — nothing in the package requires a signature (non-interest
  // checking, no checks, no POD). The server completed the signing session as
  // delivery-only at submit; this step only has to show that and hand off to
  // the pipeline view as soon as the cart leaves 'awaiting-signatures'.
  const deliveryOnly = !pkgLoading && docCount > 0 && dispatcherDocs.every((d) => d.requiresSignature === false);
  const startDisabled = pkgLoading || !pkg || docCount === 0 || noSigners || signingActive;

  // Cart-type + branch decisions (Bill 2026-07-08). Online/RCP carts are
  // detected by cart.origin === 'online' (mirrors CartDetail.isOnlineImport).
  // The remote path only "goes remote" when the remote lifecycle is live
  // (REMOTE_SIGNING_ENABLED) — until Track A/0848 ships, an online cart worked
  // in the Teams shell signs IN-BRANCH, because the dispatcher runs the
  // in-branch surface regardless and there is no remote-send mechanism yet.
  // Claiming "Send for remote signing" while running in-branch capture would
  // be a silent no-op (rule #3), so gate it.
  //
  // BACKLOG-0848 (2026-09-17): an online cart is no longer forced remote. The
  // remote path posts real accounts INACTIVE and has not been run with an
  // imported cart, so the pilot signs imported applicants in person
  // (docs/pilot/2026-09-04-legacy-applicant-boarding.md P1-6). In-branch is
  // the primary action for every cart; online carts also get "Send for remote
  // signing" as an explicit second choice. The server posts INACTIVE only when
  // the chosen plan has a remote signer (signingConfig.anyRemote).
  const isOnlineCart = cart.origin === 'online';
  const offerRemote = isOnlineCart && REMOTE_SIGNING_ENABLED;
  // Single signer → instant initiate (customer present, no one else to
  // consider) with an optional Configure affordance. Everything else opens the
  // Configure modal.
  // Bill 2026-09-30: whenever a signature requirement is triggered the officer
  // marks each required signer in person or remote — so the modal opens for
  // every cart, single signer included. (The old single-signer instant start
  // gave no chance to mark the person remote.)
  const singleInBranch = signerCount === 1;
  const instantInitiate = false;
  void singleInBranch;
  const configuredBy = user?.upn ?? user?.officerCode;

  // Per-signer capture preference passed to the dispatcher (signature docs only;
  // consent/click docs are never overridden — enforced dispatcher-side).
  const capturePrefs = useMemo(() => {
    if (!resolvedConfig) return undefined;
    const m = new Map<number, 'drawn' | 'typed'>();
    for (const s of resolvedConfig.signers) m.set(s.signerIndex, s.capture);
    return m;
  }, [resolvedConfig]);

  // Load the required-signer set when the configure modal opens.
  useEffect(() => {
    if (!configureOpen) return;
    let cancelled = false;
    setRequiredLoading(true);
    (async () => {
      try {
        const res = await client.retail.disclosures.resolve({ cartId: cart.cartId }, { signal: makeSignal() });
        if (cancelled) return;
        const roster = dispatcherSigners.map((_, i) => (i === 0 && pkg?.signers[0]?.Role === 'Primary' ? 'Primary' : (pkg?.signers[i]?.Role ?? (i === 0 ? 'Primary' : `Joint${i}`))));
        const set = new Set<number>();
        for (const row of res.resolved) {
          if (!row.requiresSignature) continue;
          for (const role of row.signerRoles) {
            const idx = roster.findIndex((r) => r.toLowerCase() === role.toLowerCase());
            if (idx >= 0) set.add(idx);
          }
        }
        setRequiredSignerIndexes(set);
      } catch {
        // Unknown → treat everyone as required (over-ask, never under-ask).
        if (!cancelled) setRequiredSignerIndexes(undefined);
      } finally {
        if (!cancelled) setRequiredLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configureOpen, cart.cartId]);

  async function handleStart(cfg: SigningSessionConfig): Promise<void> {
    if (startDisabled || starting) return;
    setStarting(true);
    setStartBeganAt(Date.now());
    setStartPhase('plan');
    setStartError(null);
    setResolvedConfig(cfg);
    try {
      // Persist the officer's per-signer plan on cart.metadataJson.signingConfig BEFORE prepare so
      // the commit pipeline can branch in-branch vs remote at resume. BACKLOG-1761 (Bill 2026-10-15):
      // its own route - the cart update route refused it once the cart was locked for signing
      // (CART_CONTENT_LOCKED on every attempt, smoke cart 0a7a4d92), so the server never knew who
      // signed remotely. NON-FATAL: the live-remote-link stop-gap still covers a failed save.
      try {
        const saved = await client.retail.signing.internal.savePlan(
          { cartId: cart.cartId, signingConfig: cfg },
          { signal: makeSignal() },
        );
        if (!saved.success) {
          // eslint-disable-next-line no-console
          console.warn('[SignStep] signing plan save refused for cart', cart.cartId, saved.errorCode ?? saved.error ?? '(no detail)');
        }
      } catch (persistErr) {
        // eslint-disable-next-line no-console
        console.warn('[SignStep] signing plan save failed for cart', cart.cartId, persistErr);
      }

      // Fetch the SERVER's signable document list (ids the capture endpoint
      // accepts). signerIndex 0 — the doc list is cart-wide; per-signer
      // engine differences are handled inside the dispatcher per pair.
      setStartPhase('prepare');
      const prep = await client.retail.signing.internal.prepare({
        cartId: cart.cartId,
        signerIndex: 0,
        surface,
      });
      if (!prep.success || !prep.documents || prep.documents.length === 0) {
        setStartError(
          prep.error ??
            'The signing package is not ready. Re-submit the application to rebuild it, then try again.',
        );
        return;
      }
      setServerDocs(
        prep.documents.map((d): DocInfo => ({
          documentInstanceId: d.documentId,
          docTypeCode: d.docTypeCode,
          documentTitle: d.displayName,
          captureMode: d.acceptableCaptureMode,
          // View-only disclosures arrive as 'review' items (Bill 2026-09-30);
          // an older server omits the flag, which means signable.
          requiresSignature: d.requiresSignature ?? true,
        })),
      );
      setPresentationMode(prep.presentationMode ?? null);
      setSignerPortalBaseUrl(prep.signerPortalBaseUrl ?? null);
      setReviewSigners(prep.reviewSignerIndexes ? new Set(prep.reviewSignerIndexes) : undefined);

      // ── Remote signers (0832) — send an emailed link + access code per
      // remote signer, out-of-band. The package the invite self-drives is the
      // one prepare() just cached. In-branch signers still use the dispatcher.
      const remoteConfigs = cfg.signers.filter((s) => s.mode === 'remote');
      if (remoteConfigs.length > 0) {
        setStartPhase('invites');
        const rows: RemoteInviteRow[] = [];
        for (const sc of remoteConfigs) {
          // A signer who already has a live link (sent earlier, or already finished) keeps it -
          // starting the session again must not e-mail them a second one.
          const live = remoteInvites.find((r) => r.signerIndex === sc.signerIndex && !r.cancelled && !r.error);
          if (live) {
            rows.push(live);
            continue;
          }
          const signer = dispatcherSigners.find((d) => d.signerIndex === sc.signerIndex);
          const name = signer?.displayName
            ?? [signer?.firstName, signer?.lastName].filter(Boolean).join(' ')
            ?? `Signer ${sc.signerIndex + 1}`;
          const email = sc.email ?? signer?.email ?? '';
          if (!email) {
            rows.push({ signerIndex: sc.signerIndex, name, error: 'No email on file — capture one on the People step.' });
            continue;
          }
          try {
            const res = await client.retail.signing.remote.invite({
              cartId: cart.cartId,
              signerIndex: sc.signerIndex,
              signerName: name,
              signerCif: signer?.cifNumber,
              email,
            });
            if (res.success) {
              rows.push({
                signerIndex: sc.signerIndex, name, email,
                ...(res.presentationId ? { presentationId: res.presentationId } : {}),
                link: res.link, accessCode: res.accessCode,
                emailSent: res.emailSent, expiresAt: res.expiresAt,
              });
            } else {
              rows.push({ signerIndex: sc.signerIndex, name, email, error: res.error ?? 'Invite failed.' });
            }
          } catch {
            rows.push({ signerIndex: sc.signerIndex, name, email, error: 'Invite failed — network error.' });
          }
        }
        setRemoteInvites(rows);
      }
      const allRemote = cfg.signers.length > 0 && remoteConfigs.length === cfg.signers.length;
      setFullyRemote(allRemote);

      // Fully-remote cart → no in-branch dispatcher; the customers sign via
      // the link. Mixed / all-in-branch → mount the dispatcher (in-branch signers only).
      if (!allRemote) setSigningActive(true);
      emit({
        eventType: 'signing.session.started',
        eventCategory: 'signing',
        cartId: cart.cartId,
        payload: {
          sessionId: cart.cartId, // dispatcher uses cartId-as-session for internal-engine pairs
          docCount: prep.documents.length,
          signerCount,
          cohortKey,
          surface,
          mode: pkg?.mode,
          // Signing-session config (Bill 2026-07-08) — so downstream telemetry
          // + the pipeline see the in-branch/remote split without re-reading it.
          anyRemote: cfg.anyRemote,
          signerModes: cfg.signers.map((s) => ({ signerIndex: s.signerIndex, mode: s.mode })),
        },
      });
      onSigningStarted?.(cart.cartId);
    } catch (err) {
      setStartError(
        err instanceof Error ? err.message : 'Could not start the signing session. Try again.',
      );
    } finally {
      setStarting(false);
      setStartPhase(null);
      setStartBeganAt(null);
    }
  }

  // Overlay steps for the session start.
  const startSteps: ProcessingStep[] = useMemo(() => {
    const order: Array<{ key: 'plan' | 'prepare' | 'invites'; label: string; detail?: string }> = [
      { key: 'plan', label: 'Saving the signing plan', detail: 'who signs in person, who signs remotely' },
      { key: 'prepare', label: 'Preparing the documents for signature', detail: `${docCount} document${docCount === 1 ? '' : 's'}` },
      ...(resolvedConfig?.anyRemote ? [{ key: 'invites' as const, label: 'Sending remote signing links', detail: 'emailed link + one-time code' }] : []),
    ];
    const idx = order.findIndex((o) => o.key === startPhase);
    return order.map((o, i) => ({
      key: o.key,
      label: o.label,
      ...(o.detail ? { detail: o.detail } : {}),
      state: startPhase === null ? 'pending' : i < idx ? 'done' : i === idx ? 'active' : 'pending',
    }));
  }, [startPhase, docCount, resolvedConfig]);

  // Overlay steps for post-signature processing: the pipeline's post-sign
  // steps plus the granular Core Director calls the server reports.
  const finishSteps: ProcessingStep[] = useMemo(() => {
    const keys: Array<{ key: string; label: string }> = [
      { key: 'complete', label: 'Closing the signing session and stamping signatures' },
      { key: 'acct-add', label: 'Opening the account(s) in Core Director' },
      { key: 'beneficiary-attach', label: 'Recording beneficiaries and relationships' },
      { key: 'post-sign-jx', label: 'Debit cards, NetTeller and account services' },
      { key: 'notifications', label: 'Filing documents and sending notifications' },
      { key: 'cart-resolution-stamp', label: 'Finalizing the application' },
    ];
    const byKey = new Map((pipelineStatus?.steps ?? []).map((s) => [s.stepKey as string, s]));
    const started = completeState?.ok === true;
    return keys.map((k): ProcessingStep => {
      if (k.key === 'complete') {
        return { key: k.key, label: k.label, state: completeState === null ? 'active' : completeState.ok ? 'done' : 'error',
                 ...(completeState && !completeState.ok && completeState.message ? { detail: completeState.message } : {}) };
      }
      const s = byKey.get(k.key);
      const st = s?.status as string | undefined;
      const gran = (pipelineStatus?.granularSteps ?? []).filter((g) => g.state === 'done' || g.state === 'pending' || g.state.startsWith('error'));
      const active = gran.find((g) => g.state === 'pending');
      const detail = st === 'running' && active ? active.plainLabel : s?.errorMessage ?? undefined;
      const state: ProcessingStep['state'] =
        st === 'completed' || st === 'succeeded' || st === 'done' ? 'done'
        : st === 'failed' || st === 'error' ? 'error'
        : st === 'skipped' ? 'skipped'
        : st === 'running' || st === 'in-progress' ? 'active'
        : started && !pipelineStatus ? (k.key === 'acct-add' ? 'active' : 'pending')
        : 'pending';
      return { key: k.key, label: k.label, state, ...(detail ? { detail } : {}) };
    });
  }, [pipelineStatus, completeState]);

  const finishing = (deliveryOnly || allComplete) && !pipelineHandedOff
    && (completeState === null || completeState.ok === true) && !(completeState?.skipped);
  useEffect(() => {
    if (finishing && finishBeganAt === null) setFinishBeganAt(Date.now());
    if (!finishing && finishBeganAt !== null) setFinishBeganAt(null);
  }, [finishing, finishBeganAt]);

  // ── Live pipeline telemetry after signing (Bill 2026-09-17) ───────────────
  // The FSR must see that work is happening, what it is doing, and that they
  // should not interrupt it. Polls the same /commit/status the Pipeline screen
  // uses, and hands off (onAllSigned) the moment the cart leaves
  // 'awaiting-signatures' so CartDetail swaps to the full Pipeline view.
  const handedOffRef = useRef<boolean>(false);
  useEffect(() => {
    const drive = deliveryOnly
      || (allComplete && completeState?.ok === true && (!completeState.skipped || completeState.waitingRemote === true));
    if (!drive) return;
    const waiting = completeState?.waitingRemote === true;
    let stop = false;
    const startedAt = Date.now();
    const tick = async (): Promise<void> => {
      try {
        const snap = await client.retail.carts.getCommitStatus(cart.cartId);
        if (stop) return;
        setPipelineStatus(snap);
        if (snap.cartStatus && snap.cartStatus !== 'awaiting-signatures') {
          if (!handedOffRef.current) {
            handedOffRef.current = true;
            setPipelineHandedOff(true);
            onAllSigned?.(cart.cartId);
          }
          stop = true;
          return;
        }
      } catch {
        // A missed poll is not a failure — the next tick may succeed.
      }
      if (!stop && !waiting) setPipelineSlow(Date.now() - startedAt > 120_000);
    };
    void tick();
    // Waiting on a remote signer can take hours; check less often.
    const id = window.setInterval(() => { void tick(); }, waiting ? 15_000 : 2_000);
    return () => { stop = true; window.clearInterval(id); };
  }, [allComplete, completeState, client, cart.cartId, onAllSigned, deliveryOnly]);

  const retryComplete = useCallback(async (): Promise<void> => {
    if (retryingComplete) return;
    setRetryingComplete(true);
    try {
      const resp = await client.retail.signing.internal.complete({ cartId: cart.cartId });
      setCompleteState(resp.success
        ? { ok: true }
        : { ok: false, message: resp.error ?? resp.code ?? undefined });
    } catch (err) {
      setCompleteState({ ok: false, message: err instanceof Error ? err.message : undefined });
    } finally {
      setRetryingComplete(false);
    }
  }, [retryingComplete, client, cart.cartId]);

  function handleAllComplete(): void {
    setAllComplete(true);
    emit({
      eventType: 'signing.session.completed',
      eventCategory: 'signing',
      cartId: cart.cartId,
      payload: {
        sessionId: cart.cartId,
        docCount,
        signerCount,
        cohortKey,
      },
    });
    // The hand-off now waits for the signing session to actually close — the
    // poll above calls onAllSigned once the pipeline starts (BACKLOG-1482).
  }

  function handleCancel(): void {
    setSigningActive(false);
  }

  return (
    <section aria-labelledby="rao-sign-h" className="rao-sign">
      <div>
        <h2 id="rao-sign-h" className="rao-sign__heading">
          Signing
        </h2>
        {/* CHG-0055 — follows signing.commit.waitForSignatures (the Cart Approved window says the same). */}
        <p className="rao-sign__intro">
          {openingNeverWaits(config.systemSettings)
            ? 'The accounts open without waiting for signatures. Each signer signs in branch on the signing screen or remotely from an e-mailed link; anyone who has not signed yet stays on your follow-up list until they do.'
            : 'Signatures are collected before the accounts open. Each signer signs in branch on the signing screen or remotely from an e-mailed link; the accounts open on their own once everyone has signed.'}
        </p>
      </div>

      {/* BACKLOG-1750 order of operations: who's signing → documents (collapsed) → signing → done. */}
      <RemoteInvitesPanel
        invites={remoteInvites}
        onInviteUpdated={(row) => setRemoteInvites((prev) => prev.map((x) => (x.signerIndex === row.signerIndex ? row : x)))}
      />

      <DisclosurePackagePanel
        pkg={pkg}
        loading={pkgLoading}
        error={pkgError}
        onRefresh={refreshPackage}
        parties={disclosureParties}
        accounts={disclosureAccounts}
        cartId={cart.cartId}
        built={builtManifest ?? null}
      />

      {noSigners && pkg && (
        <Banner tone="warn">
          No signers found on the cart. Add at least a <strong>Primary</strong>{' '}
          owner on the People step before starting signatures.
        </Banner>
      )}

      {startError && !signingActive && (
        <Banner tone="err">
          We couldn&apos;t start signing: {startError}
        </Banner>
      )}

      {/* Hide the initiate row once a fully-remote cart's invites are out
          (there's no in-branch dispatcher to mount — the panel above is the
          live state). Mixed carts still mount the dispatcher below. */}
      {deliveryOnly && !pipelineHandedOff && (
        <Banner tone="ok">
          No signatures are required for this application. The disclosures were delivered
          with the package and the account opening is finishing on the server.
        </Banner>
      )}
      {!signingActive && !allComplete && !deliveryOnly && !(fullyRemote && remoteInvites.length > 0) && (
        <div className="rao-sign__actions">
          {instantInitiate ? (
            <>
              {/* Single signer → instant in-branch initiate with a default
                  config; the modal is skipped. Configure stays available. */}
              <Button
                variant="primary"
                onClick={() =>
                  void handleStart(buildDefaultConfig(dispatcherSigners, false, configuredBy))
                }
                disabled={startDisabled || starting}
              >
                {starting
                  ? 'Starting…'
                  : pkgLoading
                    ? 'Preparing package…'
                    : noSigners
                      ? 'Waiting for signers'
                      : docCount === 0
                        ? 'No documents to sign'
                        : offerRemote
                          ? 'Initiate signing (in branch)'
                          : 'Initiate signing'}
              </Button>
              {singleInBranch && !startDisabled && !starting && (
                <Button variant="link" onClick={() => setConfigureOpen(true)}>
                  Configure
                </Button>
              )}
            </>
          ) : (
            // Multi-signer in-branch / mixed → configure before initiating.
            <Button
              variant="primary"
              onClick={() => setConfigureOpen(true)}
              disabled={startDisabled || starting}
            >
              {starting
                ? 'Starting…'
                : pkgLoading
                  ? 'Preparing package…'
                  : noSigners
                    ? 'Waiting for signers'
                    : docCount === 0
                      ? 'No documents to sign'
                      : 'Configure Signing Session'}
            </Button>
          )}
          {/* BACKLOG-0848 — online carts: remote is a deliberate second
              choice, never the default. */}
          {offerRemote && !startDisabled && !starting && (
            <Button
              variant="ghost"
              onClick={() =>
                void handleStart(buildDefaultConfig(dispatcherSigners, true, configuredBy))
              }
            >
              Send for remote signing
            </Button>
          )}
        </div>
      )}

      <ConfigureSigningSessionModal
        open={configureOpen}
        signers={dispatcherSigners}
        isOnlineCart={false}
        existingConfig={existingConfig}
        {...(configuredBy ? { configuredBy } : {})}
        {...(requiredSignerIndexes ? { requiredSignerIndexes } : {})}
        requiredLoading={requiredLoading}
        onClose={() => setConfigureOpen(false)}
        onConfirm={(cfg) => {
          setConfigureOpen(false);
          void handleStart(cfg);
        }}
      />

      {/* Blocking progress (Bill 2026-09-30): the officer sees exactly what the
          server is doing and cannot click into a half-configured session. */}
      <ProcessingOverlay
        open={starting}
        title="Preparing the signing session"
        message={startPhase === 'prepare' ? 'Rendering and checking each document — this can take a moment the first time after a system update.' : undefined}
        steps={startSteps}
        startedAt={startBeganAt}
      />
      <ProcessingOverlay
        open={finishing}
        title={deliveryOnly ? 'Finishing the account opening — no signatures required' : 'Finishing the account opening'}
        message={pipelineSlow ? 'This is taking longer than usual. The work continues on the server; do not restart the application.' : 'Signatures are in. Posting to Core Director now.'}
        steps={finishSteps}
        startedAt={finishBeganAt}
        note="Keep this window open until the account numbers appear. The pipeline resumes automatically; nothing needs to be clicked."
      />

      {signingActive && !allComplete && serverDocs && inBranchSigners.length > 0 && (
        <SigningSurfaceDispatcher
          cartId={cart.cartId}
          signers={inBranchSigners}
          docs={serverDocs}
          surface={surface}
          cohortKey={cohortKey}
          {...(presentationMode ? { presentationMode } : {})}
          {...(signerPortalBaseUrl ? { signerPortalBaseUrl } : {})}
          {...(capturePrefs ? { signerCapturePrefs: capturePrefs } : {})}
          {...(reviewSigners ? { reviewSignerIndexes: reviewSigners } : {})}
          {...(user?.officerCode ? { officerCode: user.officerCode } : {})}
          onAllComplete={handleAllComplete}
          onCompleteResult={handleCompleteResult}
          onCancel={handleCancel}
        />
      )}

      {/* BACKLOG-1482 — what actually happened after the last signature. */}
      {allComplete && completeState === null && (
        <Banner tone="info">
          <BusyLine text="Saving the signatures. Don't close this application." />
        </Banner>
      )}

      {allComplete && completeState?.ok === false && (
        <Banner tone="err" title="The signatures are saved, but the accounts have NOT been opened">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <span>
              We couldn&rsquo;t close the signing session, so the account opening never
              started{completeState.message ? ` (${completeState.message})` : ''}. Nothing is
              lost — the signatures are on file. Try again; if it keeps failing, contact
              support with this application open.
            </span>
            <div>
              <Button variant="primary" onClick={() => { void retryComplete(); }} disabled={retryingComplete}>
                {retryingComplete ? 'Trying again…' : 'Try again'}
              </Button>
            </div>
          </div>
        </Banner>
      )}

      {allComplete && completeState?.waitingRemote === true && (
        <Banner tone="info" title="Signed in branch — waiting on the remote signer">
          Everyone signing in branch is done. The accounts open on their own as soon as{' '}
          {remoteInvites.filter((x) => !x.cancelled && !x.error && x.stage !== 'finished').map((x) => x.name).join(', ') || 'the remote signer'}{' '}
          finishes from the e-mailed link. You can close this application; their progress shows above.
        </Banner>
      )}

      {allComplete && completeState?.ok === true && completeState.skipped === true && !completeState.waitingRemote && (
        <Banner tone="ok">
          All signatures captured. The remote signing service closes this session when
          the customer finishes; the accounts open after that.
        </Banner>
      )}

      {allComplete && completeState?.ok === true && !completeState.skipped && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <Banner tone="info" title="Opening the accounts">
            <BusyLine text="All signatures are captured. Stay on this screen and don't close the application — this usually takes a minute or two, and the steps below update as each one finishes." />
          </Banner>
          {pipelineSlow && (
            <Banner tone="warn">
              Still working. It has been more than two minutes. Leave it running; if it
              doesn&rsquo;t finish, the application page offers a Retry.
            </Banner>
          )}
          {/* BACKLOG-1750: the step list shows in the "Finishing the account opening" window above,
              then in the opening screen's processing history - not as a permanent list here. */}
        </div>
      )}
    </section>
  );
}
